import { useState, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Bot, Upload, FileText, Loader2, AlertTriangle, CheckCircle, BookOpen } from "lucide-react";

/**
 * AIDocumentAnalysis - Button + dialog for quick AI analysis of uploaded documents.
 * @param {string} context - Optional context for the analysis (e.g., "DRC Submission #123")
 * @param {string} analysisType - "drc_review" | "complaint_review" | "general"
 * @param {string} buttonText - Text for the trigger button
 * @param {string} buttonVariant - Button variant
 */
const AIDocumentAnalysis = ({
  context = "",
  analysisType = "general",
  buttonText = "Analyze with AI",
  buttonVariant = "outline"
}) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [additionalContext, setAdditionalContext] = useState(context);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleAnalyze = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (additionalContext) formData.append("context", additionalContext);
      formData.append("analysis_type", analysisType);

      const res = await axios.post(`${API}/ai-document-analysis/analyze`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 120000
      });

      setResult(res.data);
      toast.success("Analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setAdditionalContext(context);
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        size="sm"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="ai-analyze-btn"
      >
        <Bot className="w-4 h-4 mr-1" /> {buttonText}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" /> AI Document Analysis
            </DialogTitle>
            <DialogDescription>
              Upload a document for AI cross-reference against HOA governing documents. Supports PDF, DOCX, and TXT.
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              {/* File Upload */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  file ? "border-green-300 bg-green-50" : "border-muted-foreground/25 hover:border-primary/50"
                }`}
                onClick={() => fileRef.current?.click()}
                data-testid="ai-analysis-file-drop"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{file.name}</span>
                    <span className="text-sm text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm font-medium">Click to select a document</p>
                    <p className="text-xs text-muted-foreground">PDF, DOCX, TXT — Max 50 MB</p>
                  </>
                )}
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.doc,.txt,.csv,.md"
                  onChange={e => setFile(e.target.files?.[0] || null)} data-testid="ai-analysis-file-input" />
              </div>

              <div>
                <Label>Additional Context (Optional)</Label>
                <Textarea
                  value={additionalContext}
                  onChange={e => setAdditionalContext(e.target.value)}
                  placeholder="e.g., 'This is a letter from owner requesting a variance to the fence height restriction...'"
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={handleAnalyze} disabled={!file || loading} data-testid="run-ai-analysis-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                  {loading ? "Analyzing..." : "Run Analysis"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Result Header */}
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Analysis Complete</p>
                  <p className="text-xs text-green-700">
                    {result.filename} — {result.text_extracted?.toLocaleString()} chars extracted
                    {result.rag_context_used && " — RAG context used"}
                  </p>
                </div>
              </div>

              {/* Governance Disclaimer */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  This AI analysis follows Horse Mountain Ranch document hierarchy (Declaration &gt; Articles &gt; Bylaws &gt; Policies &gt; Rules &gt; Design Guidelines). It does not constitute legal advice.
                </p>
              </div>

              {/* Analysis Content */}
              <div className="prose prose-sm max-w-none bg-card border rounded-lg p-4 overflow-y-auto max-h-[400px]" data-testid="ai-analysis-result">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {result.analysis}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={reset}>Analyze Another</Button>
                <Button onClick={() => setOpen(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIDocumentAnalysis;
